/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useState } from 'react';
import { Form } from 'react-final-form';
import { Button } from '@blend/alchemy';

import { editTeamInfo } from 'blb-api/groups';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { AlertBar, Modal } from 'blb-components/GrommetComponents';
import { BlendBridge } from 'blb-types/blendBridge';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';

import TeamInfoForm from './CreateTeamModal/TeamInfoForm';

const ModalStyles = {
  content: {
    height: 'fit-content',
    width: '500px',
  },
};

interface EditTeamInfoModalProps {
  handleClose: (shouldRefetch?: boolean) => Promise<void>;
  team: BlendBridge.API.SearchGroupRecord;
}

const EditTeamInfoModal: React.FC<EditTeamInfoModalProps> = ({ handleClose, team }) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const me = useContext(MeContext)!;
  const Toaster = useToasts()!;

  const onSubmit = async (teamInfo: BlendBridge.API.PatchGroupBody) => {
    setIsSubmitting(true);
    setError(undefined);
    try {
      await editTeamInfo(team.id, teamInfo);
      Toaster.addToast({
        message: `Team ${teamInfo.name} successfully updated.`,
        theme: 'info',
      });
      // Not putting this in a finally block to avoid updating state if the modal closes
      setIsSubmitting(false);
      await handleClose(true);
    } catch (e) {
      const err = e as Error;
      setError(err);
      setIsSubmitting(false);
    }
  };
  return (
    <Form
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting }) => (
        <Modal
          buttonComponents={
            <Button
              disabled={isSubmitting || submitting}
              id="it-edit-team-info-submit"
              label="Save"
              onClick={handleSubmit}
              variant="primary"
            />
          }
          handleClose={handleClose}
          id="it-edit-team-info-modal"
          isOpen
          style={ModalStyles}
          title="Edit team info"
        >
          {error && (
            <AlertBar
              boxProps={{ margin: { bottom: 'medium' } }}
              errorMessage={`We hit a snag while saving your changes. Please try again or contact support at ${
                getPersona(me.principalType).getSupportContactInfo().email
              }`}
              id="it-edit-group-info-error"
            />
          )}
          {isSubmitting ? <CenteredLoadingSpinner /> : <TeamInfoForm teamInfo={{ name: team.name, ...team.address }} />}
        </Modal>
      )}
    ></Form>
  );
};

export default EditTeamInfoModal;
